import './WebshopProduct.scss'

import {wrap} from '@codeurs/front'
import React, {createRef, Fragment, useEffect} from 'react'
import {useLocation} from 'react-router'
import {JSONLD, Product as JSONLDProduct} from 'react-structured-data'
import {ImageType, PageData, WysiwygType} from '../../../types'
import {useTranslation} from '../../../util/i18n'
import {style} from '../../../util/style'
import {Block, Blocks, BlockType} from '../../blocks/Blocks'
import {Breadcrumbs} from '../../layout/Breadcrumbs'
import {
	Contentside,
	ContentsideContent,
	ContentsideSidebar
} from '../../layout/Contentside'
import {HeroSide, HeroType} from '../../layout/Hero'
import {Layout} from '../../layout/Layout'
import {Overview} from '../../layout/Overview'
import {SidebarBlocks, SidebarBlockType} from '../../sidebar/SidebarBlocks'
import {OptionType} from '../../ui/Dropdown'
import {NewsletterProduct} from '../layout/NewsletterProduct'
import {ProductCard} from '../layout/product/ProductCard'

const styles = style('webshopproduct')

export type ProductTaxonomyType = {
	groups?: Array<PageData>
	variations?: ProductVariationType
}
export type ProductVariationType = {
	id: string
	title: string
	path: string
	options: Array<OptionType>
	filteredOptions?: Array<OptionType>
}

export type ProductType = {
	channel: 'product_detail_downloadable' | 'product_detail_physical'
	id: number
	title: string
	path: string
	url: string
	preview: ProductPreviewType
	hero?: {
		title?: string
		text?: WysiwygType
	}
	article_code: string
	selection_code: string
	status: ProductStatusTypes
	stock?: number
	category?: ProductCategoryTypes
	product_groups?: Array<number>
	product_filters?: Array<number>
	price?: string
	is_free?: boolean
	quantity_discount?: Array<ProductQuantityDiscount>
	variations?: Array<ProductVariationType>
	featured?: boolean
	info?: boolean
}
export type ProductPreviewType = {
	title?: string
	text?: string
	image?: ImageType
	blur?: boolean
}
export type ProductStatusTypes =
	| 'available'
	| 'last_pieces'
	| 'temporarily_unavailable'
	| 'sold_out'
	| 'coming_soon'

type ProductPageType = {
	hero: HeroType
	contentblocks?: Array<BlockType>
	sidebarblocks?: Array<
		SidebarBlockType | {type: 'sizechart'} | {type: 'printdemand'}
	>
}

export type ProductCategoryTypes = 'physical' | 'digital'

export type ProductQuantityDiscount = {
	quantity: string
	price_per_piece: string
}

export const WebshopProduct: React.FC<PageData & ProductPageType> = wrap(
	Layout
)(({...props}) => {
	const {
		parents,
		title,
		hero,
		contentblocks,
		sidebarblocks,
		related,
		card,
		id: productId,
		preview,
		url,
		price,
		article_code,
		selection_code,
		status
	} = props

	const newsletterRef = createRef<HTMLDivElement>()
	const img = preview.image
	const absoluteImage = img ? location.hostname + img.src : null
	const t = useTranslation('webshop').products
	const loc = useLocation()
	const allowScroll =
		status === 'temporarily_unavailable' && loc?.hash === '#newsletterproduct'

	useEffect(() => {
		const newsletter = newsletterRef?.current
		if (newsletter && allowScroll) {
			scrollTo({behavior: 'smooth', top: newsletter?.offsetTop})
		}
	}, [])

	const hasProductCard = contentblocks?.some(
		(block) => block.type === 'productcard'
	)

	const processedContentBlocks: Array<BlockType> = contentblocks.map(
		(block) => {
			if (block.type === 'productcard')
				return {
					type: 'custom',
					render: () => (
						<Block>
							<ProductCard {...card} />
							{status === 'temporarily_unavailable' && (
								<div ref={newsletterRef}>
									<NewsletterProduct
										productId={productId}
										text={`<p><b>${t.notify.info}</b></p>`}
										submit={t.notify.submit}
									/>
								</div>
							)}
						</Block>
					)
				}
			return block
		}
	)

	return (
		<div className={styles()}>
			<Breadcrumbs parents={parents} title={title} />
			<Contentside>
				<ContentsideContent>
					<HeroSide {...hero} title={hero?.title || title} />
					{!hasProductCard && (
						<div className={styles('product')}>
							<ProductCard {...card} />
							{status === 'temporarily_unavailable' && (
								<div ref={newsletterRef}>
									<NewsletterProduct
										productId={productId}
										text={`<p><b>${t.notify.info}</b></p>`}
										submit={t.notify.submit}
									/>
								</div>
							)}
						</div>
					)}
					<Blocks
						blocks={processedContentBlocks}
						className={styles('blocks')}
					/>
				</ContentsideContent>
				<ContentsideSidebar>
					<SidebarBlocks blocks={sidebarblocks} />
				</ContentsideSidebar>
			</Contentside>
			{related && (
				<Overview title={t.related} layout={'product'} items={related} />
			)}
			<JSONLD>
				<JSONLDProduct
					name={title}
					description={preview.text}
					image={[absoluteImage]}
					brand={'Broederlijk delen'}
					sku={article_code}
					gtin={selection_code}
					offers={{
						url: price ? url : null,
						priceCurrency: price ? 'EUR' : null,
						price: price ? price : null,
						availability: getSchemaOrgAvailability(status as ProductStatusTypes)
					}}
				/>
			</JSONLD>
		</div>
	)
})

const getSchemaOrgAvailability = (status: ProductStatusTypes): string => {
	switch (status) {
		case 'available':
			return 'InStock'
		case 'last_pieces':
			return 'LimitedAvailability'
		case 'sold_out':
			return 'SoldOut'
		case 'temporarily_unavailable':
		case 'coming_soon':
			return 'OutOfStock'
	}
}
